import { RefObject, useCallback, useEffect, useMemo, useRef } from "react";

import type { Event } from "@tsmono/inspect-common/types";
import {
  useExtendedFind,
  useFindTargetSetter,
  type ExtendedFindFn,
  type FindDirection,
} from "@tsmono/react/components";
import {
  useEventListener,
  useLatestRef,
  useUnmount,
} from "@tsmono/react/hooks";

import type { SwimlaneRow } from "../timeline/swimlaneRows";
import type { TranscriptViewNodesHandle } from "../TranscriptViewNodes";

import {
  buildEventToRowMap,
  findAllMatches,
  SampleMatch,
} from "./sampleSearch";

const DEFAULT_ID = "transcript-sample";
const SETTLE_LIMIT = 90;

export interface UseTranscriptSearchSourceOptions {
  events: Event[];
  rows: SwimlaneRow[];
  selected: string | null;
  onSelect: (rowKey: string | null) => void;
  viewNodesRef: RefObject<TranscriptViewNodesHandle | null>;
  /** Suppress headroom-driven swimlane collapse during programmatic scrolls.
   *  Without this, scrolling between matches makes the swimlane bar flicker
   *  collapsed→expanded as the headroom hook misreads our scroll as a user
   *  gesture. Pass `true` to enable the debounced lock; the hook releases it
   *  automatically once the scroll settles. */
  onHeadroomResetAnchor?: (debounce?: boolean) => void;
  /** Force the headroom hidden state to match the search direction so a
   *  Next press collapses the swimlane (like a manual scroll-down) and a
   *  Prev press reveals it (like a scroll-up). Without this, the headroom
   *  hook only reflects whatever residual scroll motion useScrollDirection
   *  detected, which doesn't reliably correspond to the user's intent. */
  onHeadroomSetHidden?: (hidden: boolean) => void;
  /** Stable registration ID. Default `"transcript-sample"`. */
  id?: string;
}

/**
 * Registers a sample-wide search source with ExtendedFindContext.
 *
 * - count(term): findAllMatches over the full sample. Cached per term.
 * - searchFn(term, dir): finds the next match across the entire sample,
 *   switches swimlane row if needed, sets the find target (auto-expand),
 *   then delegates to viewNodesRef.scrollToEvent.
 *
 * Preconditions: must be mounted inside an `ExtendedFindProvider`. The
 * `FindTargetProvider` is optional — its setter no-ops when absent.
 */
export function useTranscriptSearchSource(
  options: UseTranscriptSearchSourceOptions
): void {
  const {
    events,
    rows,
    selected,
    onSelect,
    viewNodesRef,
    onHeadroomResetAnchor,
    onHeadroomSetHidden,
    id = DEFAULT_ID,
  } = options;
  const { registerVirtualList, registerMatchCounter } = useExtendedFind();
  const setFindTarget = useFindTargetSetter();

  const eventToRow = useMemo(() => buildEventToRowMap(rows), [rows]);

  const cacheRef = useRef<{
    events: Event[];
    eventToRow: Map<string, string>;
    term: string;
    matches: SampleMatch[];
  } | null>(null);
  const getMatches = useCallback(
    (term: string): SampleMatch[] => {
      const c = cacheRef.current;
      if (
        c &&
        c.events === events &&
        c.eventToRow === eventToRow &&
        c.term === term
      ) {
        return c.matches;
      }
      const matches = findAllMatches(events, term, eventToRow);
      cacheRef.current = { events, eventToRow, term, matches };
      return matches;
    },
    [events, eventToRow]
  );

  // Read across `await` boundaries to detect "user is already on this row"
  // mid-search; needs to be a ref so a row-switch in flight sees the update.
  const selectedRef = useLatestRef(selected);

  const lastResolvedRef = useRef<{ match: SampleMatch; term: string } | null>(
    null
  );
  const invocationIdRef = useRef(0);
  // Self-correction timers scheduled at the end of searchFn. Tracked so
  // unmount can clear them — otherwise they fire against detached DOM.
  const pendingTimersRef = useRef<Set<number>>(new Set());
  useUnmount(() => {
    for (const t of pendingTimersRef.current) clearTimeout(t);
    pendingTimersRef.current.clear();
  });

  // Active search term. Set by countFn on every keystroke so the listener
  // below has it on the first selectionchange after a fresh search.
  const activeTermRef = useRef<string>("");
  // Keep `lastResolvedRef` in sync when window.find advances within a row
  // without going through our searchFn. Otherwise the next cross-row
  // navigation would pickNext from a stale position.
  useEventListener(
    typeof document === "undefined" ? null : document,
    "selectionchange",
    () => {
      const term = activeTermRef.current;
      if (!term) return;
      const sel = document.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      // Cheap pre-filter: a real find result is a single text node selection
      // exactly the length of the term. Skip the expensive `range.toString()`
      // serialization for everything else (Ctrl-A on a long transcript can
      // make every keystroke in the find box block on MB-sized text).
      if (range.startContainer !== range.endContainer) return;
      if (range.endOffset - range.startOffset !== term.length) return;
      if (range.toString().toLowerCase() !== term.toLowerCase()) return;
      const matches = getMatches(term);
      const match = matchAtSelection(matches, term);
      if (match) lastResolvedRef.current = { match, term };
    }
  );

  const countFn = useCallback(
    (term: string): number => {
      // FindBand calls this on every keystroke before any navigation, so it's
      // the natural place to record the active term for the selection
      // listener — earlier than searchFn, which only fires on cross-row jumps.
      activeTermRef.current = term;
      return getMatches(term).length;
    },
    [getMatches]
  );

  const searchFn: ExtendedFindFn = useCallback(
    async (term, direction, onContentReady) => {
      const myId = ++invocationIdRef.current;
      const isStale = () => myId !== invocationIdRef.current;

      const matches = getMatches(term);
      if (matches.length === 0) return false;
      activeTermRef.current = term;

      // Match the headroom UI to the user's search direction (forward press
      // collapses the swimlane like manual scroll-down; backward expands it),
      // and lock the scroll-direction tracker so its observations of the
      // imminent imperative scroll don't fight us.
      onHeadroomResetAnchor?.(true);
      onHeadroomSetHidden?.(direction === "forward");

      let position = resolvePosition(
        matches,
        lastResolvedRef.current,
        viewNodesRef.current,
        selectedRef.current,
        term
      );

      // Iterate forward/backward until we find a match whose panel actually
      // mounts. Some events (deeply nested under collapsed subtask spans, or
      // filtered out of the rendered tree entirely) can be counted by
      // buildEventToRowMap but never reach the DOM — without this skip, the
      // user gets stuck at the boundary with no way forward. Cap attempts so
      // a totally unreachable cluster doesn't spin for 30s+.
      const SKIP_LIMIT = Math.min(matches.length, 8);
      let next: SampleMatch | null = null;
      for (let attempt = 0; attempt < SKIP_LIMIT; attempt++) {
        next = pickNext(matches, position, direction);

        if (next.rowKey !== selectedRef.current) {
          onSelect(next.rowKey);
          const ready = await waitForRow(viewNodesRef, next.eventId);
          if (isStale()) return false;
          if (!ready) {
            position = matches.indexOf(next);
            lastResolvedRef.current = { match: next, term };
            continue;
          }
        }

        setFindTarget({ term, eventId: next.eventId });
        await raf();
        if (isStale()) return false;
        await raf();
        if (isStale()) return false;

        viewNodesRef.current?.scrollToEvent(next.eventId);
        const inDom = await waitForEventInDOM(next.eventId);
        if (isStale()) return false;
        if (inDom) break;

        // Unreachable event — advance past ALL matches sharing this eventId.
        // A single nested-but-unrendered event typically has many occurrences;
        // trying each would burn SKIP_LIMIT on identical failures.
        const skippedEventId = next.eventId;
        let lastSkipIdx = matches.indexOf(next);
        const stride = direction === "forward" ? 1 : -1;
        for (
          let idx = lastSkipIdx + stride;
          idx >= 0 &&
          idx < matches.length &&
          matches[idx]!.eventId === skippedEventId;
          idx += stride
        ) {
          lastSkipIdx = idx;
        }
        position = lastSkipIdx;
        lastResolvedRef.current = { match: matches[lastSkipIdx]!, term };
        next = null;
      }
      if (!next) return false;

      // Position the cursor so FindBand's subsequent `window.find` lands on
      // the term inside OUR chosen panel. window.find advances FROM the
      // current selection (it does NOT match at the cursor itself), so
      // collapse JUST BEFORE the term going forward and JUST AFTER going
      // backward. Bails silently if the term isn't rendered in the panel
      // (e.g. a JSON-stringified field) — window.find then picks whatever.
      positionSelectionAroundTerm(next.eventId, term, direction);

      lastResolvedRef.current = { match: next, term };
      onContentReady();

      // The transcript renders many code blocks that get syntax-highlighted
      // asynchronously (Prism splitting `<code>` text into many spans), and
      // many ExpandablePanels that re-render on `setFindTarget`. Either
      // can detach the text node `window.find` just anchored on, silently
      // collapsing the highlight ~hundreds of ms after the search lands.
      // Re-establish the selection after settling, but only if the current
      // highlight is missing or wrong (no-op when it survives naturally).
      const reselectId = next.eventId;
      const timer = window.setTimeout(() => {
        if (isStale()) return;
        reselectTermInPanel(reselectId, term);
      }, 300);
      pendingTimersRef.current.add(timer);
      return true;
    },
    [
      getMatches,
      viewNodesRef,
      onSelect,
      setFindTarget,
      onHeadroomResetAnchor,
      onHeadroomSetHidden,
      selectedRef,
    ]
  );

  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    const unCount = registerMatchCounter(id, countFn);
    const unSearch = registerVirtualList(id, searchFn);
    return () => {
      unCount();
      unSearch();
    };
  }, [id, registerMatchCounter, registerVirtualList, countFn, searchFn]);
}

function pickNext(
  matches: SampleMatch[],
  position: number,
  dir: FindDirection
): SampleMatch {
  const len = matches.length;
  // `position` is the index of the "current" match (or -1 if none).
  if (position < 0) {
    return dir === "forward" ? matches[0]! : matches[len - 1]!;
  }
  return dir === "forward"
    ? matches[(position + 1) % len]!
    : matches[(position - 1 + len) % len]!;
}

function resolvePosition(
  matches: SampleMatch[],
  last: { match: SampleMatch; term: string } | null,
  view: TranscriptViewNodesHandle | null,
  selected: string | null,
  term: string
): number {
  // Prefer the last-resolved match when the term hasn't changed.
  if (last && last.term === term) {
    const idx = matches.findIndex(
      (m) =>
        m.eventId === last.match.eventId &&
        m.fieldKey === last.match.fieldKey &&
        m.fieldIndex === last.match.fieldIndex &&
        m.occurrenceIndex === last.match.occurrenceIndex
    );
    if (idx !== -1) return idx;
  }
  // Fallback: first match in the currently-visible row.
  const range = view?.getVisibleRange();
  const flattened = view?.getFlattenedNodes() ?? [];
  if (!range || flattened.length === 0) return -1;
  const visibleIds = new Set(
    flattened.slice(range.startIndex, range.endIndex + 1).map((n) => n.id)
  );
  const idx = matches.findIndex(
    (m) => m.rowKey === selected && visibleIds.has(m.eventId)
  );
  return idx;
}

/**
 * Find the SampleMatch corresponding to the current document selection, if any.
 *
 * Walks up from the selection's startContainer to find an event-panel element
 * (one whose `id` is in `matches`'s eventId set). Then counts how many
 * occurrences of `term` precede the selection within that event's text — that
 * count is the DOM-order occurrence index, which we map to the n-th match in
 * our array for that event.
 *
 * Returns `null` if there is no selection, no event ancestor, or the count
 * runs past the matches we know about (e.g. selection isn't actually on a
 * `term` instance).
 */
function matchAtSelection(
  matches: SampleMatch[],
  term: string
): SampleMatch | null {
  if (typeof window === "undefined" || !term) return null;
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);

  const eventIds = new Set(matches.map((m) => m.eventId));
  let el: Element | null =
    range.startContainer instanceof Element
      ? range.startContainer
      : range.startContainer.parentElement;
  while (el && !eventIds.has(el.id)) el = el.parentElement;
  if (!el) return null;
  const eventId = el.id;

  const lowered = term.toLowerCase();
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let occurrenceInEvent = 0;
  let node: Node | null;
  while ((node = walker.nextNode())) {
    if (!(node instanceof Text)) continue;
    const textNode = node;
    if (textNode === range.startContainer) {
      const head = textNode.data.slice(0, range.startOffset).toLowerCase();
      let from = 0;
      while ((from = head.indexOf(lowered, from)) !== -1) {
        occurrenceInEvent++;
        from += lowered.length;
      }
      break;
    }
    const text = textNode.data.toLowerCase();
    let from = 0;
    while ((from = text.indexOf(lowered, from)) !== -1) {
      occurrenceInEvent++;
      from += lowered.length;
    }
  }

  let seen = 0;
  for (const m of matches) {
    if (m.eventId !== eventId) continue;
    if (seen === occurrenceInEvent) return m;
    seen++;
  }
  return null;
}

/**
 * Walk the DOM under the event element with `eventId` and place a collapsed
 * selection adjacent to the FIRST occurrence of `term` (forward) or the LAST
 * occurrence (backward), so FindBand's subsequent `window.find` advances onto
 * exactly that occurrence.
 *
 * Forward: cursor BEFORE the first match — `window.find` searches forward
 * from the cursor and lands on the term.
 * Backward: cursor AFTER the last match — `window.find` with backward=true
 * searches backward from the cursor and lands on the term. (If we collapsed
 * before instead, backward would skip past it and either find nothing or
 * land in unrelated DOM, which makes findExtendedInDOM return false and the
 * counter fail to update.)
 *
 * If the panel isn't mounted or doesn't render the term as text (e.g. the
 * match was in a JSON-stringified field we don't render), bail silently —
 * FindBand will fall back to its default windowFind behavior.
 */
function positionSelectionAroundTerm(
  eventId: string,
  term: string,
  direction: FindDirection
): boolean {
  const root = document.getElementById(eventId);
  if (!root) return false;
  const lowered = term.toLowerCase();
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let target: { node: Text; idx: number } | null = null;
  for (let node; (node = walker.nextNode());) {
    if (!(node instanceof Text)) continue;
    const textNode = node;
    const text = textNode.data.toLowerCase();
    let from = 0;
    while ((from = text.indexOf(lowered, from)) !== -1) {
      target = { node: textNode, idx: from };
      from += lowered.length;
      if (direction === "forward") break;
    }
    if (target && direction === "forward") break;
  }
  if (!target) return false;
  const sel = window.getSelection();
  if (!sel) return false;
  const range = document.createRange();
  range.setStart(
    target.node,
    direction === "forward" ? target.idx : target.idx + term.length
  );
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
  return true;
}

/**
 * If the current selection no longer covers `term` inside the panel
 * (because a late settling pass — virtual-list re-render, lazy syntax
 * highlighting, ExpandablePanel auto-expand reflow — detached the text
 * node `window.find` was anchored on), re-anchor the selection to the
 * first occurrence of `term` in the panel. Returns false (no-op) when
 * the existing highlight is intact.
 */
function reselectTermInPanel(eventId: string, term: string): boolean {
  const root = document.getElementById(eventId);
  if (!root) return false;
  const sel = window.getSelection();
  if (!sel) return false;
  if (
    sel.rangeCount > 0 &&
    !sel.getRangeAt(0).collapsed &&
    sel.getRangeAt(0).toString().toLowerCase() === term.toLowerCase() &&
    root.contains(sel.getRangeAt(0).startContainer)
  ) {
    return true; // existing highlight is intact — don't disturb
  }
  const lowered = term.toLowerCase();
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node; (node = walker.nextNode());) {
    const idx = (node.textContent ?? "").toLowerCase().indexOf(lowered);
    if (idx === -1) continue;
    const range = document.createRange();
    range.setStart(node, idx);
    range.setEnd(node, idx + term.length);
    sel.removeAllRanges();
    sel.addRange(range);
    return true;
  }
  return false;
}

function raf(): Promise<void> {
  return new Promise((resolve) =>
    typeof requestAnimationFrame !== "undefined"
      ? requestAnimationFrame(() => resolve())
      : setTimeout(resolve, 0)
  );
}

/**
 * Wait for the freshly-selected row to mount: poll until the target eventId
 * is present in the flattened-node list, or the budget expires.
 * Returns false if the view is not mounted or the event never appears.
 */
async function waitForRow(
  viewNodesRef: RefObject<TranscriptViewNodesHandle | null>,
  eventId: string
): Promise<boolean> {
  for (let i = 0; i < SETTLE_LIMIT; i++) {
    const view = viewNodesRef.current;
    if (!view) {
      // No mounted view to wait on — bail immediately.
      return false;
    }
    if (view.getFlattenedNodes().some((n) => n.id === eventId)) return true;
    await raf();
  }
  return false;
}

/**
 * Wait until the event panel is actually rendered to the DOM. After
 * `scrollToEvent` triggers a virtual-list scroll for an off-screen target, the
 * panel takes several frames to mount. Returns false on timeout. The budget
 * is shorter than for row mount because we use this to detect unreachable
 * matches and skip them — too long a wait makes skipping feel laggy.
 */
async function waitForEventInDOM(eventId: string): Promise<boolean> {
  if (typeof document === "undefined") return false;
  const DOM_BUDGET = 30;
  for (let i = 0; i < DOM_BUDGET; i++) {
    if (document.getElementById(eventId)) return true;
    await raf();
  }
  return false;
}
